import { useCallback, useEffect, useState } from "react";
import {
  loadContacts,
  addContact as apiAddContact,
  updateContact as apiUpdateContact,
  deleteContact as apiDeleteContact,
} from "../api/contactsApi";
import { AUTH_STORAGE_KEY } from "../socket";

// Server GET /api/contacts returns:
//   { id: contactId (email), contactId, name (fullName resolved), emails, profileImageUrl }
// So contact.id IS the email — use it directly as the API key.
function transformContact(contact, index) {
  const cid = contact.id || contact.contactId || `server_${index}`;
  return {
    id: cid,
    contactId: cid,
    emails: contact.emails || [contact.contactId],
    // BEFORE: contact.name || contact.alias || contact.contactId
    // AFTER:  alias first, then server-resolved name, then email
    name: contact.alias || contact.name || contact.contactId || "",
    profileImageUrl: contact.profileImageUrl || "",
    group: "",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

function useContacts() {
  const [contacts, setContacts] = useState([]);

  const getAuthHeaders = () => {
    try {
      const raw = localStorage.getItem(AUTH_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed?.token ? { Authorization: `Bearer ${parsed.token}` } : null;
    } catch (e) {
      return null;
    }
  };

  useEffect(() => {
    const loadServerContacts = async () => {
      let authHeaders = null;
      try {
        const raw = localStorage.getItem(AUTH_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        if (parsed?.token) authHeaders = { Authorization: `Bearer ${parsed.token}` };
      } catch (e) {
        console.error("Failed to get auth headers:", e);
      }

      if (!authHeaders) {
        const stored = localStorage.getItem("mail-contacts");
        if (stored) {
          try {
            const parsed = JSON.parse(stored);
            if (Array.isArray(parsed)) setContacts(parsed);
          } catch (e) {}
        }
        return;
      }

      try {
        const serverContacts = await loadContacts(authHeaders);
        setContacts(serverContacts.map(transformContact));
      } catch (error) {
        console.error("Failed to load server contacts:", error);
        const stored = localStorage.getItem("mail-contacts");
        if (stored) {
          try { setContacts(JSON.parse(stored)); } catch (e) {}
        }
      }
    };

    loadServerContacts();
  }, []);

  useEffect(() => {
    const handler = () => {
      const authHeaders = getAuthHeaders();
      if (!authHeaders) return;
      loadContacts(authHeaders)
        .then((sc) => setContacts(sc.map(transformContact)))
        .catch((err) => console.error("Failed to refresh contacts:", err));
    };
    window.addEventListener("contacts-refresh", handler);
    return () => window.removeEventListener("contacts-refresh", handler);
  }, []);

  useEffect(() => {
    const listener = (e) => {
      try {
        const detail = e?.detail;
        if (!detail) {
          const authHeaders = getAuthHeaders();
          if (authHeaders) {
            loadContacts(authHeaders)
              .then(sc => setContacts(sc.map(transformContact)))
              .catch(err => console.error("Failed to reload contacts:", err));
          }
          return;
        }
        const { contactId, alias, name } = detail;
        if (!contactId) return;
        const normalized = String(contactId).toLowerCase();
        setContacts((prev) => {
          const exists = prev.some((c) =>
            (Array.isArray(c.emails) ? c.emails.map(x => String(x).toLowerCase()) : [])
              .includes(normalized)
          );
          if (exists) return prev;
          return [...prev, {
            id: normalized,
            emails: [normalized],
            name: name || alias || "",
            group: "",
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          }];
        });
      } catch (err) {}
    };
    window.addEventListener("contacts-updated", listener);
    return () => window.removeEventListener("contacts-updated", listener);
  }, []);

  useEffect(() => {
    localStorage.setItem("mail-contacts", JSON.stringify(contacts));
  }, [contacts]);

  const addContact = useCallback(async (contactData) => {
    const { emails, email, name = "", group = "" } = contactData || {};
    const names = String(name || "").trim();
    const grp = String(group || "").trim();

    let list = [];
    if (Array.isArray(emails)) {
      list = emails.map(e => String(e || "").trim().toLowerCase()).filter(Boolean);
    } else if (typeof email === "string" && email.trim()) {
      list = [email.trim().toLowerCase()];
    } else {
      return null;
    }

    const authHeaders = getAuthHeaders();
    if (authHeaders) {
      try {
        for (const emailAddr of list) {
          await apiAddContact({ contactId: emailAddr, alias: names }, authHeaders);
        }
        const serverContacts = await loadContacts(authHeaders);
        setContacts(serverContacts.map(transformContact));
        return serverContacts[serverContacts.length - 1]?.id;
      } catch (error) {
        throw error; // already an Error with server message
      }
    }

    const newContact = {
      id: Date.now().toString(),
      emails: list,
      name: names,
      group: grp,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setContacts(prev => [...prev, newContact]);
    return newContact.id;
  }, []);

  const updateContact = useCallback(async (id, updates) => {
    const authHeaders = getAuthHeaders();

    if (authHeaders) {
      try {
        // ✅ FIX: id IS the email/contactId — pass directly, no 'server_' prefix logic
        await apiUpdateContact(id, { alias: updates.name || updates.alias || "" }, authHeaders);
        const serverContacts = await loadContacts(authHeaders);
        setContacts(serverContacts.map(transformContact));
        return;
      } catch (error) {
        console.error("Failed to update contact on server:", error);
        throw error;
      }
    }

    setContacts(prev =>
      prev.map(c =>
        c.id === id
          ? {
              ...c,
              emails: updates.emails ? updates.emails : updates.email ? [updates.email] : c.emails,
              group: updates.group !== undefined ? updates.group : c.group,
              name: updates.name !== undefined ? updates.name : c.name,
              updatedAt: new Date().toISOString(),
            }
          : c
      )
    );
  }, []);

  const deleteContact = useCallback(async (id) => {
    const authHeaders = getAuthHeaders();

    if (authHeaders) {
      try {
        // ✅ FIX: id IS the email/contactId — pass directly, no 'server_' prefix logic
        await apiDeleteContact(id, authHeaders);
        const serverContacts = await loadContacts(authHeaders);
        setContacts(serverContacts.map(transformContact));
      } catch (error) {
        console.error("Failed to delete contact:", error);
        throw error;
      }
      return;
    }

    setContacts(prev => prev.filter(c => c.id !== id));
  }, [contacts]);

  const getContactByEmail = useCallback((email) => {
    if (!email) return undefined;
    const normalized = String(email).toLowerCase();
    return contacts.find(c => {
      const cEmails = Array.isArray(c.emails)
        ? c.emails.map(e => e.toLowerCase())
        : c.email ? [String(c.email).toLowerCase()] : [];
      return cEmails.includes(normalized);
    });
  }, [contacts]);

  return { contacts, addContact, updateContact, deleteContact, getContactByEmail };
}

export default useContacts;